import { useState, useCallback, useEffect, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry } from 'ag-grid-community'
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise'
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise'

import Spinner from 'components/antD/spin'
import { getProgramList } from 'api/program.api'
import { getTutorList } from 'api/tutor.api'
import { getToolsInventoryList } from 'api/inventory.api'
import { getToolsRentalGrid } from 'api/rental.api'

import styles from './agGrid.module.scss'

ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsEnterpriseModule)])
LicenseManager.setLicenseKey(
    'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-090332}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Domfam_Co.,_Ltd.}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{20_July_2026}____[v3]_[0102]_MTc4NDUwMjAwMDAwMA==f9eb0f450dab63a75694821bc1436af6',
)

const AgGrid = ({ pageType, gridType, colDefs, defaultColDef, region }) => {
    //  state
    const [agGridListData, setAgGridListData] = useState([])
    const [isLoading, setLoading] = useState(true)
    const gridRef = useRef(null)

    const handleRowClick = event => {
        const tutorId = event.data.tutor_id
        const programId = event.data.program_id
        const inventoryId = event.data.tools_id
        const rentalId = event.data.rental_id
        if (rentalId) {
            window.open(`/tools/rental/${rentalId}`, '_blank', 'fullscreen=yes')
            return
        }
        if (tutorId) {
            window.open(`/tutors/${tutorId}`, '_blank', 'fullscreen=yes')
        }
        if (programId) {
            window.open(`/program/${programId}`, '_blank', 'fullscreen=yes')
        }
        if (inventoryId) {
            window.open(`/tools/inventory/${inventoryId}`, '_blank', 'fullscreen=yes')
        }
    }

    // api
    const handleFetchData = useCallback(async () => {
        try {
            setLoading(true)
            let allData

            switch (pageType) {
                case 'program': {
                    const { data } = await getProgramList({ pageNo: 0, pageSize: 0, regionId: region })
                    const programs = data?.data?.programs
                    if (programs) {
                        allData = programs
                    }
                    break
                }
                case 'tutor': {
                    const { data } = await getTutorList({ pageNo: 0, pageSize: 0, regionId: region })
                    const tutors = data?.data?.tutors
                    if (tutors) {
                        allData = tutors
                    }
                    break
                }
                case 'inventory': {
                    const { data } = await getToolsInventoryList({ pageNo: 0, pageSize: 0 })
                    const tools = data?.data?.tools
                    if (tools) {
                        allData = tools
                    }
                    break
                }
                case 'rental': {
                    const { data } = await getToolsRentalGrid()
                    const rentals = data?.data
                    if (rentals) {
                        allData = rentals
                    }
                    break
                }
                case 'statistics_program':
                case 'statistics_tutor':
                    break
                default: {
                    break
                }
            }
            if (allData) {
                setAgGridListData(allData)
            }
        } catch (e) {
            console.error('Unexpected error:', e)
        } finally {
            setLoading(false)
        }
    }, [pageType])

    useEffect(() => {
        handleFetchData()
    }, [handleFetchData])

    return (
        <div className={styles.container} style={{ height: gridType === 'pivot' ? '1000px' : 'auto' }}>
            {isLoading ? (
                <div className={styles.spin_wrap}>
                    <Spinner />
                </div>
            ) : (
                <>
                    {gridType === 'normal' && (
                        <AgGridReact
                            ref={gridRef}
                            rowData={agGridListData}
                            columnDefs={colDefs}
                            defaultColDef={defaultColDef}
                            domLayout="autoHeight"
                            pagination
                            cellSelection
                            rowGroupPanelShow="always"
                            enableCharts
                            onRowClicked={handleRowClick}
                            onGridReady={params => {
                                params.api.setFilterModel({
                                    registration_year: {
                                        filterType: 'set',
                                        values: ['2025'],
                                    },
                                })
                            }}
                        />
                    )}
                    {gridType === 'pivot' && (
                        <AgGridReact
                            ref={gridRef}
                            rowData={agGridListData}
                            columnDefs={colDefs}
                            defaultColDef={defaultColDef}
                            sideBar={['columns', 'filters']}
                            pivotPanelShow="always"
                            rowGroupPanelShow="always"
                            cellSelection
                            enableCharts
                            statusBar={{
                                statusPanels: [
                                    { statusPanel: 'agTotalRowCountComponent', align: 'left' },
                                    { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
                                    { statusPanel: 'agFilteredRowCountComponent' },
                                    { statusPanel: 'agSelectedRowCountComponent' },
                                    { statusPanel: 'agAggregationComponent' },
                                ],
                            }}
                        />
                    )}
                </>
            )}
        </div>
    )
}
export default AgGrid
